import type { CaptainRole } from '../../types/captain'
import { ROLE_LABEL } from '../../types/captain'

export interface RosterEntryRow {
  role: CaptainRole
  displayName: string | null
  /** ISO timestamp or null when not available. */
  signedUpAt: string | null
}

interface Props {
  slots: { role: CaptainRole; target: number }[]
  entries: RosterEntryRow[]
}

interface RenderRow {
  role: CaptainRole
  name: string | null
  signedUpAt: string | null
}

function formatSignupDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Asia/Hong_Kong',
    day: '2-digit',
    month: 'short',
  })
}

export default function RosterTable({ slots, entries }: Props) {
  // Group entries by role so we can pair them with each role's target.
  const entriesByRole = new Map<CaptainRole, RosterEntryRow[]>()
  for (const e of entries) {
    const arr = entriesByRole.get(e.role) ?? []
    arr.push(e)
    entriesByRole.set(e.role, arr)
  }

  const rows: RenderRow[] = []
  for (const slot of slots) {
    const filled = entriesByRole.get(slot.role) ?? []
    // ON-334: render a row per actual sign-up first — this covers overflow
    // captains beyond the target, who must stay visible to the organizer —
    // then pad with "awaiting volunteer" placeholder rows only for the
    // remaining gap up to the target (never truncate real sign-ups to it).
    for (const entry of filled) {
      rows.push({
        role: slot.role,
        name: entry.displayName,
        signedUpAt: entry.signedUpAt,
      })
    }
    for (let i = filled.length; i < slot.target; i++) {
      rows.push({ role: slot.role, name: null, signedUpAt: null })
    }
  }

  return (
    <div
      style={{
        border: '0.5px solid rgba(244,242,242,0.08)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ background: 'rgba(244,242,242,0.02)' }}>
            <th style={headStyle}>Role</th>
            <th style={headStyle}>Captain</th>
            <th style={{ ...headStyle, textAlign: 'right' }}>Signed up</th>
            <th style={{ ...headStyle, textAlign: 'right' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              style={{
                borderTop: '0.5px solid rgba(244,242,242,0.06)',
              }}
            >
              <td style={cellStyle}>
                <span
                  style={{
                    fontFamily:
                      "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                    fontSize: 11,
                    color: 'rgba(198,196,185,0.7)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {ROLE_LABEL[r.role]}
                </span>
              </td>
              <td style={cellStyle}>
                {r.name ? (
                  <span style={{ color: '#F4F2F2' }}>{r.name}</span>
                ) : (
                  <span
                    style={{
                      color: 'rgba(198,196,185,0.45)',
                      fontStyle: 'italic',
                      fontFamily: "'Lora', Georgia, serif",
                    }}
                  >
                    awaiting volunteer
                  </span>
                )}
              </td>
              <td
                style={{
                  ...cellStyle,
                  textAlign: 'right',
                  fontFamily:
                    "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                  fontSize: 11,
                  color: 'rgba(198,196,185,0.55)',
                  letterSpacing: '0.04em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.signedUpAt ? formatSignupDate(r.signedUpAt) : '—'}
              </td>
              <td style={{ ...cellStyle, textAlign: 'right' }}>
                {r.name ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily:
                        "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                      fontSize: 10,
                      color: '#C2E530',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#C2E530',
                      }}
                    />
                    Confirmed
                  </span>
                ) : (
                  <span
                    style={{
                      fontFamily:
                        "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                      fontSize: 10,
                      color: 'rgba(198,196,185,0.45)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    Open
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const headStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 16px',
  fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  color: 'rgba(198,196,185,0.55)',
}

const cellStyle: React.CSSProperties = {
  padding: '14px 16px',
}
